import { DRONE_ASSET_ID, DRONE_ID, DRONE_FLEET_CONFIG, isDroneSnapshotList, unpackDroneSnapshot,
  type DroneId, type DroneRenderSnapshot, type PackedDrone, type ServerDroneTelemetry } from "@endclock/shared";
import type { AIAssetLibrary, LoadedAIAsset } from "../ai/asset-library.js";
import type { Camera } from "../engine/camera.js";
import type { ActorContactShadows } from "../engine/actor-contact-shadows.js";
import { writeUprightTransform } from "../engine/math.js";
import { raySphereDistance, screenRay, terrainOccludesRay } from "../engine/picking.js";
import type { Renderer } from "../engine/renderer.js";
import { interpolateDroneTransform, type InterpolatedDroneTransform } from "./interpolation.js";
import { DroneScannerEffect } from "./scanner.js";

const GROUNDED_FLIGHT_STATES = new Set(["LANDED", "CHARGING", "LANDING", "EMERGENCY_LANDING"]);
export const DRONE_VISUAL_CONFIG = Object.freeze({ maximumScanners: 6, maximumCaptions: 4 });
interface DroneActor {
  current: DroneRenderSnapshot;
  previous: DroneRenderSnapshot;
  previousAt: number;
  currentAt: number;
  readonly transform: InterpolatedDroneTransform;
  readonly caption: HTMLDivElement;
  telemetry: ServerDroneTelemetry | null;
  visible: boolean;
  scannerDrawCalls: number;
}

/** Nearby identities share the existing GLB/GPU asset and one instanced draw
 * per primitive. Interpolation, speech and telemetry remain per identity. */
export class DroneWorld {
  captionMaximumX: number | null = null;
  selectedId: DroneId | null = null;
  private readonly actors = new Map<DroneId, DroneActor>();
  private readonly visibleActors: DroneActor[] = [];
  private readonly shadowActors: DroneActor[] = [];
  private readonly instanceData = new Float32Array(DRONE_FLEET_CONFIG.count * 20);
  private readonly scanner: DroneScannerEffect;
  private readonly prototypeCaption: HTMLDivElement;
  private readonly emptyTransform = { x: 0, y: 0, z: 0, yaw: 0 };
  private currentAt = 0;
  private receivedSnapshot = false;
  private serverNow = 0;
  private scannerDrawCalls = 0;

  constructor(private readonly renderer: Renderer, readonly library: AIAssetLibrary, private readonly parent: HTMLElement) {
    this.scanner = new DroneScannerEffect(renderer);
    this.prototypeCaption = this.createCaption(DRONE_ID);
  }

  // Preserve the prototype diagnostic seam used by existing gameplay smoke.
  get current(): DroneRenderSnapshot | null { return this.stateFor(DRONE_ID); }
  get telemetry(): ServerDroneTelemetry | null { return this.telemetryFor(DRONE_ID); }
  get transform(): InterpolatedDroneTransform { return this.actors.get(DRONE_ID)?.transform ?? this.emptyTransform; }
  stateFor(id: string | null): DroneRenderSnapshot | null { return id ? this.actors.get(id as DroneId)?.current ?? null : null; }
  telemetryFor(id: string | null): ServerDroneTelemetry | null { return id ? this.actors.get(id as DroneId)?.telemetry ?? null : null; }

  ingest(drones: readonly PackedDrone[], at: number): void {
    if (!Number.isFinite(at) || this.receivedSnapshot && at <= this.currentAt || !isDroneSnapshotList(drones)) return;
    this.receivedSnapshot = true; this.currentAt = at;
    const present = new Set<DroneId>();
    for (const packed of drones) {
      const next = unpackDroneSnapshot(packed); present.add(next.id);
      const actor = this.actors.get(next.id);
      if (actor) {
        actor.previous = actor.current; actor.previousAt = actor.currentAt;
        actor.current = next; actor.currentAt = at;
      } else this.actors.set(next.id, { current: next, previous: next, previousAt: at, currentAt: at,
        transform: { ...next.position, yaw: next.yaw }, caption: next.id === DRONE_ID ? this.prototypeCaption : this.createCaption(next.id),
        telemetry: null, visible: false, scannerDrawCalls: 0 });
    }
    for (const [id, actor] of this.actors) if (!present.has(id)) {
      actor.caption.hidden = true; actor.visible = false;
      if (id !== DRONE_ID) actor.caption.remove();
      this.actors.delete(id);
    }
    if (this.selectedId && !this.actors.has(this.selectedId)) this.selectedId = null;
    if (this.actors.size === 0) this.library.setDynamicTotals(0, 0);
  }

  setTelemetry(message: ServerDroneTelemetry): void {
    const actor = this.actors.get(message.droneId);
    if (actor && (!actor.telemetry || message.at >= actor.telemetry.at)) actor.telemetry = message;
  }

  reset(): void {
    for (const [id, actor] of this.actors) {
      actor.caption.hidden = true;
      if (id !== DRONE_ID) actor.caption.remove();
    }
    this.actors.clear(); this.visibleActors.length = 0; this.shadowActors.length = 0;
    this.selectedId = null; this.currentAt = 0; this.receivedSnapshot = false; this.scannerDrawCalls = 0;
    this.library.setDynamicTotals(0, 0); this.prototypeCaption.hidden = true;
  }

  update(serverNow: number): void {
    this.serverNow = serverNow;
    for (const actor of this.actors.values()) {
      interpolateDroneTransform(actor.previous, actor.current, actor.previousAt, actor.currentAt, serverNow, actor.transform);
      if (!GROUNDED_FLIGHT_STATES.has(actor.current.flightState)) {
        actor.transform.y += Math.sin(serverNow * .0017 + actor.current.home.x * .013 + actor.current.home.z * .017) * .025;
      }
    }
  }

  render(camera: Camera): void {
    this.visibleActors.length = 0;
    for (const actor of this.actors.values()) actor.visible = false;
    const asset = this.library.assets.get(DRONE_ASSET_ID);
    if (this.library.state !== "ready" || !asset) return;
    this.library.setDynamicTotals(this.actors.size, asset.triangleCount * this.actors.size);
    for (const actor of this.actors.values()) {
      const t = actor.transform;
      if (!camera.isSphereVisible(t.x, t.y + asset.boundsCenterY, t.z, asset.boundsRadius + .03)) continue;
      actor.visible = true; this.visibleActors.push(actor);
    }
    this.drawBatch(asset, this.visibleActors);
    this.renderer.metrics.visibleAiObjects += this.visibleActors.length;
  }

  /** One reusable scanner resource; selected/nearby actors receive effects first
   * under separate hard scanner and caption budgets. */
  renderEffects(camera: Camera, canvas: HTMLCanvasElement): void {
    this.scannerDrawCalls = 0;
    for (const actor of this.actors.values()) { actor.scannerDrawCalls = 0; actor.caption.hidden = true; }
    this.visibleActors.sort((a, b) => Number(b.current.id === this.selectedId) - Number(a.current.id === this.selectedId)
      || distanceSquared(a.transform, camera.position) - distanceSquared(b.transform, camera.position)
      || a.current.id.localeCompare(b.current.id));
    let scans = 0, captions = 0;
    for (const actor of this.visibleActors) {
      if (!actor.visible) continue;
      if (actor.current.scanner.mode !== "idle" && scans < DRONE_VISUAL_CONFIG.maximumScanners) {
        this.scanner.render(camera, actor.transform, actor.current.scanner, this.serverNow);
        actor.scannerDrawCalls = this.scanner.drawCalls; this.scannerDrawCalls += this.scanner.drawCalls; scans += 1;
      }
      const speech = actor.telemetry?.lastSpeech;
      if (!speech || speech.expiresAt <= this.serverNow || captions >= DRONE_VISUAL_CONFIG.maximumCaptions) continue;
      const point = this.screenPosition(actor, camera, canvas);
      if (!point?.visible) continue;
      const caption = actor.caption, text = `${actor.current.displayName} · ${speech.text}`;
      caption.hidden = false;
      if (caption.textContent !== text) caption.textContent = text;
      caption.dataset.mode = speech.mode;
      caption.style.left = `${Math.max(130, Math.min(point.x, this.captionMaximumX ?? Infinity))}px`;
      caption.style.top = `${Math.max(70, point.y - 34)}px`;
      captions += 1;
    }
  }

  renderShadow(shadowCamera: Camera): void {
    this.shadowActors.length = 0;
    const asset = this.library.assets.get(DRONE_ASSET_ID);
    if (this.library.state !== "ready" || !asset) return;
    for (const actor of this.actors.values()) {
      const t = actor.transform;
      if (shadowCamera.isSphereVisible(t.x, t.y + asset.boundsCenterY, t.z, asset.boundsRadius)) this.shadowActors.push(actor);
    }
    this.drawBatch(asset, this.shadowActors);
  }

  addContactShadows(shadows: ActorContactShadows): void {
    if (this.library.state !== "ready") return;
    for (const { transform: t } of this.actors.values()) shadows.addActor(t.x, t.y, t.z, .85, 9);
  }

  pick(clientX: number, clientY: number, canvas: HTMLCanvasElement, camera: Camera, heightAt: (x: number, z: number) => number): DroneId | null {
    const asset = this.library.assets.get(DRONE_ASSET_ID);
    if (this.library.state !== "ready" || !asset) return null;
    const ray = screenRay(clientX, clientY, canvas.getBoundingClientRect(), camera.viewProjection);
    if (!ray) return null;
    let id: DroneId | null = null, nearest = Infinity;
    for (const actor of this.actors.values()) {
      if (!actor.visible) continue;
      const t = actor.transform;
      const hit = raySphereDistance(ray, t.x, t.y + asset.boundsCenterY, t.z, asset.boundsRadius + .03);
      if (hit !== null && hit < nearest) { id = actor.current.id; nearest = hit; }
    }
    return id && !terrainOccludesRay(ray, nearest, heightAt) ? id : null;
  }

  snapshot(camera: Camera, canvas: HTMLCanvasElement) {
    const prototype = this.actors.get(DRONE_ID);
    return { state: this.current, renderPosition: { ...this.transform }, screen: prototype ? this.screenPosition(prototype, camera, canvas) : null,
      assetState: this.library.state, assetId: DRONE_ASSET_ID, count: this.actors.size,
      visibleCount: this.visibleActors.filter(actor => actor.visible).length,
      scannerDrawCalls: this.scannerDrawCalls, scannerLimit: DRONE_VISUAL_CONFIG.maximumScanners,
      boundsRadius: this.library.assets.get(DRONE_ASSET_ID)?.boundsRadius ?? 0, telemetry: this.telemetry,
      drones: [...this.actors.values()].map(actor => ({ id: actor.current.id, state: actor.current,
        renderPosition: { ...actor.transform }, screen: this.screenPosition(actor, camera, canvas),
        telemetry: actor.telemetry, scannerDrawCalls: actor.scannerDrawCalls })),
    };
  }

  hide(): void { this.visibleActors.length = 0; for (const actor of this.actors.values()) { actor.visible = false; actor.caption.hidden = true; } }
  dispose(): void { this.reset(); this.scanner.dispose(); this.prototypeCaption.remove(); }

  private drawBatch(asset: LoadedAIAsset, actors: readonly DroneActor[]): void {
    if (!actors.length) return;
    for (let index = 0; index < actors.length; index++) {
      const t = actors[index]!.transform;
      writeUprightTransform(this.instanceData, index * 20, t.x, t.y, t.z, t.yaw, 1);
    }
    for (const primitive of asset.primitives) {
      for (let index = 0; index < actors.length; index++) this.instanceData.set(primitive.baseColor, index * 20 + 16);
      this.renderer.drawStaticInstanced(primitive.mesh, this.instanceData, actors.length, primitive.activeMaterial, "ai");
    }
  }

  private createCaption(id: DroneId): HTMLDivElement {
    const caption = document.createElement("div");
    caption.id = id === DRONE_ID ? "drone-caption" : `drone-caption-${id}`;
    caption.className = "drone-caption"; caption.setAttribute("role", "status");
    caption.setAttribute("aria-live", "polite"); caption.hidden = true; this.parent.append(caption);
    return caption;
  }

  private screenPosition(actor: DroneActor, camera: Camera, canvas: HTMLCanvasElement): { x: number; y: number; visible: boolean } | null {
    const m = camera.viewProjection, bounds = canvas.getBoundingClientRect();
    const x = actor.transform.x, y = actor.transform.y + .43, z = actor.transform.z;
    const w = m[3]! * x + m[7]! * y + m[11]! * z + m[15]!;
    if (!Number.isFinite(w) || w <= 0) return null;
    const clipX = (m[0]! * x + m[4]! * y + m[8]! * z + m[12]!) / w;
    const clipY = (m[1]! * x + m[5]! * y + m[9]! * z + m[13]!) / w;
    const clipZ = (m[2]! * x + m[6]! * y + m[10]! * z + m[14]!) / w;
    return { x: bounds.left + (clipX + 1) * bounds.width / 2, y: bounds.top + (1 - clipY) * bounds.height / 2,
      visible: actor.visible && Math.abs(clipX) <= 1 && Math.abs(clipY) <= 1 && Math.abs(clipZ) <= 1 };
  }
}

function distanceSquared(point: InterpolatedDroneTransform, eye: readonly number[]): number {
  return (point.x - eye[0]!) ** 2 + (point.y - eye[1]!) ** 2 + (point.z - eye[2]!) ** 2;
}
